import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePreferenceScope } from "@/hooks/useStoredPreference";
import { useDebouncedSearch } from "@/hooks/useServerList";
import type { Client } from "@/pages/Tenders/types";

const empty: Client[] = [];
export const useClientLookup = (
    selectedId?: string | number | null,
    enabled = true
) => {
    const scope = usePreferenceScope();
    const [search, setSearch] = useState("");
    const [added, setAdded] = useState<Client[]>([]);
    const delayed = useDebouncedSearch(search);
    const query = useQuery({
        queryKey: ["client-options", scope, delayed, selectedId],
        enabled,
        retry: false,
        queryFn: async ({ signal }): Promise<Client[]> =>
            (
                await api.get<{ data: Client[] }>("/clients", {
                    params: {
                        lookup: 1,
                        per_page: 20,
                        search: delayed,
                        selected_id: Number(selectedId) || undefined,
                    },
                    signal,
                })
            ).data.data,
    });
    const items = query.data || empty;
    return {
        items: [
            ...added,
            ...items.filter(
                (item) => !added.some((extra) => extra.id === item.id)
            ),
        ],
        search: setSearch,
        add: (item: Client): void =>
            setAdded((previous) => [
                item,
                ...previous.filter((entry) => entry.id !== item.id),
            ]),
        loading: query.isFetching,
        error: query.error instanceof Error ? query.error.message : null,
        reload: (): void => {
            void query.refetch();
        },
    };
};
